import axios from "axios";
import { useEffect, useState } from "react";
import { Box, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  getInputLabelProps,
  getInputProps,
  PopperFullWidth,
} from "../../../common/appStyles";
import localize from "../../../common/localize";
import {
  GetDateInYYYYMMDDWithoutTimeFormat,
  isInitialDateGreaterThanFinalDate,
} from "../../../common/utils";
import Combobox from "../../../components/common/Combobox";
import ErrorMessage from "../../../components/common/ErrorMessage";
import useGlobalContext from "../../../hooks/useGlobalContext";

const AssignEvent = () => {
  const {
    // Models
    eventModel,
    PERSONAL_API,
    config,
    // Global States Modifiers
    setEventModel,
  } = useGlobalContext();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [personalData, setPersonalData] = useState(undefined);

  const getPersonalData = async () => {
    const data = await axios.get(PERSONAL_API, {
      ...config,
      headers: {
        ...config.headers,
        pagination: -1,
      },
    });

    if (data.data.Ok && data.data.Data) {
      setPersonalData(data.data.Data);
    }
  };

  const handlePersonalAssignedChange = (currentPersonal) => {
    if (!currentPersonal) {
      setEventModel({
        ...eventModel,
        PersonalAssigned: null,
        PersonalId: 0,
      });
      return;
    }

    setEventModel({
      ...eventModel,
      PersonalAssigned: currentPersonal,
      PersonalId: currentPersonal.id,
    });
  };

  const handleExpectedAttentionDateChange = (expectedSolutionDate) => {
    try {
      if (!expectedSolutionDate) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionDateValue: null,
          ExpectedSolutionDate: null,
        });
        return;
      }

      if (
        validateExpectedSolutionDates(
          expectedSolutionDate,
          eventModel.ExpectedSolutionFinalDateValue,
        )
      ) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionDateValue: expectedSolutionDate,
          ExpectedSolutionDate:
            GetDateInYYYYMMDDWithoutTimeFormat(expectedSolutionDate),
        });
      }
    } catch {
      console.log("Invalid date");
    }
  };

  const handleExpectedAttentionFinalDateChange = (expectedSolutionDate) => {
    try {
      if (!expectedSolutionDate) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionFinalDateValue: null,
          ExpectedSolutionFinalDate: null,
        });
        return;
      }

      if (
        validateExpectedSolutionDates(
          eventModel.ExpectedSolutionDateValue,
          expectedSolutionDate,
        )
      ) {
        setEventModel({
          ...eventModel,
          ExpectedSolutionFinalDateValue: expectedSolutionDate,
          ExpectedSolutionFinalDate:
            GetDateInYYYYMMDDWithoutTimeFormat(expectedSolutionDate),
        });
      }
    } catch {
      console.log("Invalid date");
    }
  };

  const validateExpectedSolutionDates = (initial, final) => {
    if (isInitialDateGreaterThanFinalDate(initial, final)) {
      setErrorMessage(localize.date_range_error);
      setShowErrorModal(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    getPersonalData();
  }, []);

  return (
    <Box style={{ width: "100%" }} className="mt-2">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      <Combobox
        isRequired
        id="PersonalIssigned"
        fieldLabel={localize.assigned_to}
        fieldOptions={personalData ?? []}
        fieldValue={eventModel?.PersonalAssigned}
        customOptionLabel={(option) =>
          `${option.Identification} - ${option.SurName} ${option.LastName}`
        }
        handleValueChange={handlePersonalAssignedChange}
        popperFullWidth={PopperFullWidth}
      />
      <div className="my-5" />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={`${localize.expected_attention_date} (${localize.start})`}
          className="w-full"
          InputLabelProps={getInputLabelProps()}
          inputProps={getInputProps()}
          PopperComponent={PopperFullWidth}
          value={eventModel?.ExpectedSolutionDate}
          onChange={handleExpectedAttentionDateChange}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                placeholder: "",
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                style: {
                  fontSize: "1em",
                  paddingBottom: "10px",
                  Height: "100%",
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
      <div className="my-5" />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={`${localize.expected_attention_date} (${localize.end})`}
          className="w-full"
          InputLabelProps={getInputLabelProps()}
          inputProps={getInputProps()}
          PopperComponent={PopperFullWidth}
          value={eventModel?.ExpectedSolutionFinalDate}
          onChange={handleExpectedAttentionFinalDateChange}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                placeholder: "",
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                style: {
                  fontSize: "1em",
                  paddingBottom: "10px",
                  Height: "100%",
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default AssignEvent;
